import { Shell } from '@/components/Shell';
import { EmptyRow } from '@/components/ui/EmptyState';

import { Pill, stateToTone } from '@/components/ui/Pill';
import { type SortState, SortTh, sortBy, toggleSort } from '@/components/ui/SortTh';
import { adminApi } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/payments')({
  beforeLoad: async () => {
    try {
      const me = await adminApi.me();
      if (me.role !== 'admin') throw redirect({ to: '/login' });
    } catch {
      throw redirect({ to: '/login' });
    }
  },
  component: PaymentsPage,
});

const STATE_OPTS = [
  { v: '', l: 'TODOS OS ESTADOS' },
  { v: 'pending', l: 'PENDENTE' },
  { v: 'succeeded', l: 'PAGO' },
  { v: 'failed', l: 'FALHADO' },
  { v: 'refunded', l: 'REEMBOLSO' },
];

const KIND_OPTS = [
  { v: '', l: 'TODOS OS TIPOS' },
  { v: 'deposit', l: 'SINAL' },
  { v: 'balance', l: 'SALDO' },
  { v: 'refund', l: 'REEMBOLSO' },
  { v: 'caucao', l: 'CAUÇÃO' },
];

const KIND_PT: Record<string, string> = {
  deposit: 'SINAL',
  balance: 'SALDO',
  refund: 'REEMBOLSO',
  caucao: 'CAUÇÃO',
};

function PaymentsPage() {
  const [state, setState] = useState('');
  const [kind, setKind] = useState('');
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'payments', state, kind],
    queryFn: () => adminApi.payments({ state: state || undefined, kind: kind || undefined }),
  });
  const dateFmt = new Intl.DateTimeFormat('pt-PT', { dateStyle: 'short', timeStyle: 'short' });
  const count = data?.paging.count ?? 0;

  // Totals
  const totalEur = data?.items.reduce((s, p) => s + p.amountEur, 0) ?? 0;
  const [sort, setSort] = useState<SortState<'date' | 'eur'>>({ key: 'date', dir: 'desc' });
  const rows = sortBy(data?.items ?? [], (p) => (sort.key === 'eur' ? p.amountEur : p.createdAt), sort.dir);

  return (
    <Shell page="PAGAMENTOS" sub={`MOVIMENTOS FINANCEIROS · ${count} REGISTOS`}>
      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {STATE_OPTS.map((s) => (
            <button
              key={s.v || 'all-state'}
              type="button"
              onClick={() => setState(s.v)}
              className={`chip${state === s.v ? ' active' : ''}`}
            >
              {s.l}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
          {KIND_OPTS.map((k) => (
            <button
              key={k.v || 'all-kind'}
              type="button"
              onClick={() => setKind(k.v)}
              className={`chip${kind === k.v ? ' active' : ''}`}
            >
              {k.l}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: '1px solid var(--lv-line)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'var(--lv-night)' }}>
              <SortTh sortKey="date" sort={sort} onSort={(k) => setSort((s) => toggleSort(s, k))}>
                DATA
              </SortTh>
              <SortTh>RESERVA</SortTh>
              <SortTh>CLIENTE</SortTh>
              <SortTh>TIPO</SortTh>
              <SortTh>MÉTODO</SortTh>
              <SortTh
                align="right"
                sortKey="eur"
                sort={sort}
                onSort={(k) => setSort((s) => toggleSort(s, k))}
              >
                EUR
              </SortTh>
              <SortTh align="right">CVE</SortTh>
              <SortTh>ESTADO</SortTh>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={8} style={{ padding: 20, color: 'var(--lv-gray-500)', fontSize: 12 }}>
                  A carregar…
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <EmptyRow colSpan={8} message="Sem pagamentos para este filtro." />
            )}
            {rows.map((p) => (
              <tr key={p.id} style={{ borderTop: '1px solid var(--lv-line)' }}>
                <td style={{ padding: '12px 16px', fontSize: 11, color: 'var(--lv-gray-500)' }}>
                  {dateFmt.format(new Date(p.createdAt))}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span
                    style={{
                      fontFamily: 'var(--lv-font-mono)',
                      fontWeight: 700,
                      fontSize: 12,
                      color: 'var(--lv-brand)',
                    }}
                  >
                    {p.bookingCode}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 12 }}>{p.customerEmail}</td>
                <td style={{ padding: '12px 16px' }}>
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      color: 'var(--lv-ink)',
                    }}
                  >
                    {KIND_PT[p.kind] ?? p.kind.toUpperCase()}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--lv-gray-500)' }}>
                  {p.method}
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <span className="display" style={{ fontSize: 18, color: 'var(--lv-ink)' }}>
                    {p.amountEur.toFixed(2)}
                  </span>
                  <span
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: '0.08em',
                      color: 'var(--lv-gray-500)',
                      marginLeft: 3,
                    }}
                  >
                    EUR
                  </span>
                </td>
                <td
                  style={{
                    padding: '12px 16px',
                    textAlign: 'right',
                    fontSize: 12,
                    color: 'var(--lv-gray-500)',
                  }}
                >
                  {p.amountCve.toFixed(0)} CVE
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <Pill label={p.state.toUpperCase()} tone={stateToTone(p.state)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {data && (
          <div
            style={{
              borderTop: '1px solid var(--lv-line)',
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: 'var(--lv-gray-500)',
            }}
          >
            <span>
              Mostrando {data.items.length} de {count}
            </span>
            <span>
              Total visível: <strong style={{ color: 'var(--lv-ink)' }}>{totalEur.toFixed(2)} EUR</strong>
            </span>
          </div>
        )}
      </div>
    </Shell>
  );
}
